import { DatePipe } from '@angular/common';
import { Component, computed, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { of, Subscription, switchMap, tap } from 'rxjs';
import { Role } from 'src/app/models/user/role.model';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { MBRDocumentsService } from 'src/app/services/mbr-documents.service';
import { UsersService } from 'src/app/services/users.service';
import { UploadDocumentsComponent } from '../../components/upload-documents/upload-documents.component';
import { BtnCellRenderer } from '../../components/btn-cell-renderer/btn-cell-renderer.component';
import { ColDef, GridApi, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { GridDocumentComponent } from '../../components/grid-document/grid-document.component';
import { GridBadgeColumnComponent } from '../../components/grid-badge-column/grid-badge-column.component';
import { baseColDef } from '../../utils';
import { BaseListComponent } from '../../components/base-list.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { AgGridWrapperComponent } from '../../components/ag-grid-wrapper/ag-grid-wrapper.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-product-mbr-detail',
    templateUrl: './product-mbr-detail.component.html',
    styleUrls: ['./product-mbr-detail.component.scss'],
    standalone: true,
    imports: [
      MatSidenavModule,
      RouterModule,
      MatIconModule,
      UploadDocumentsComponent,
      AgGridWrapperComponent,
      MatPaginatorModule,
      MatButtonModule
    ]
})
export class ProductMbrDetailComponent extends BaseListComponent<any> implements OnInit, OnDestroy {
  productId: any;
  lenderId: any;
  public canUpload: boolean = false;
  public product: { name: string } | undefined;
  @ViewChild('drawer') drawer: MatDrawer;
  @ViewChild(UploadDocumentsComponent)
  documentUploader: UploadDocumentsComponent;
  datePipe = new DatePipe('en-US');
  subscriptions: Subscription[] = [];
  loadingValidator: boolean;
  submitting: boolean = false;

  public columnDefs: ColDef[] = [
    {
      ...baseColDef,
      field: 'name',
      headerName: 'MBR Document',
      flex: 3,
      cellRenderer: GridDocumentComponent,
      valueGetter: (params: ValueGetterParams) => {
        return [params.data];
      },
    },
    {
      ...baseColDef,
      field: 'version',
      headerName: 'Version',
    },
    {
      cellRenderer: GridBadgeColumnComponent,
      sortable: false,
      valueGetter: (params: ValueGetterParams) => {
        return params.data.isCurrent
          ? [{ class: 'bg-success', value: 'active' }]
          : [];
      },
    },
    {
      ...baseColDef,
      headerName: 'Make this Default',
      width: 200,
      cellRenderer: BtnCellRenderer,
      cellClass: 'make-default',
      valueGetter: (params: ValueGetterParams) => {
        if (this.isProviderReadOnly() || params.data.isCurrent) {
          return {};
        }
        return {
          onConfirmation: this.onMakeDefault.bind(this),
          version: params.data.version,
        };
      },
    },
  ];
  gridApi: GridApi;

  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isSystemUser = computed(() => this.userRole() === Role.SystemUser);
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isLenderReadOnly = computed(() => this.userRole() === Role.LenderReadOnly);
  readonly isProviderReadOnly = computed(() => this.userRole() === Role.ProviderReadOnly);

  constructor(
    private mbrService: MBRDocumentsService,
    private userService: UsersService,
    private route: ActivatedRoute,
    private router: Router,
    private deleteDialog: DeleteConfirmationDialogService,
  ) {
    super();
  }

  ngOnInit() {
    this.productId = this.route.snapshot.paramMap.get('productId');
    this.lenderId = this.route.snapshot.paramMap.get('lenderId');
  }

  onRequestMBR(){
    if(this.isAdmin()){
      this.router.navigate(['/admin/mbr-request/create']);
    }
    else{
      this.router.navigate(['/lender/mbr-request/create']);
    }
  }

  onMakeDefault(data: any) {
    this.deleteDialog
      .openDeleteConfirmation(
        `This document will become the default MBR.`,
        'Confirmation',
        'Confirm'
      )
      .afterClosed()
      .pipe(
        switchMap((dialogClosed) => {
          if (dialogClosed){
            if(this.isAdmin() || this.isSystemUser() || this.isLender() || this.isLenderReadOnly()){
              return this.mbrService
              .makeDefaultByLender(this.productId, this.lenderId, data.version)
            }
            else{
              return this.mbrService
                .makeDefault(this.productId, data.version)
            }
          }
          else return of(false);
        })
      )
      .subscribe((deleteConfirmation) => {
          this.reload();
      });
  }

  close() {
    this.drawer.close();
  }

  public fileRemoved(fileCount: number) {
    if (fileCount === 0) {
      this.canUpload = false;
    } else {
      this.canUpload = true;
    }
  }

  public uploadingEvent(isUploading: boolean) {
    this.isLoading = isUploading;
    if (this.documentUploader.files.length > 0) {
      this.canUpload = true;
    }
  }

  public onSubmit() {
    if(!this.canUpload){
      return;
    }

    this.canUpload = false;

    var data = {
      isActiveVersion: true,
      document: {
        serverFileName: this.documentUploader.files[0].serverFileName,
        name: this.documentUploader.files[0].file.name,
      },
    };

    if(this.isAdmin() || this.isSystemUser() || this.isLender() || this.isLenderReadOnly()){
      this.mbrService
        .uploadLenderMBR(this.productId, this.lenderId, data)
        .subscribe((r) => {
          this.canUpload = true;
          this.reload();
          this.documentUploader.clearFiles();
        });
    }
    else{
      this.mbrService
        .uploadMBR(this.productId, data)
        .subscribe((r) => {
          this.reload();
          this.canUpload = true;
          this.documentUploader.clearFiles();
        });
    }
  }

  onConfirmation(result: any) {
    if (result.confirmed) {
      this.mbrService
        .makeDefaultByLender(this.productId, this.lenderId, result.id)
        .subscribe(() => {
          this.reload();
        });
    }
  }


  reload() {
    this.gridApi.showLoadingOverlay();

    if(this.isAdmin() || this.isSystemUser() || this.lenderId){
      const subscription = this.mbrService
      .getByProductAndLender(this.productId, this.lenderId)
      .subscribe((r: any) => {
          this.gridApi.hideOverlay();
          this.rowData = r.data.documents;
          this.product = r.data.product;
          this.totalCount = r.data.documents.length
        }
      );
      this.subscriptions.push(subscription);
    }
    else {
      const subscription = this.mbrService
      .getByProduct(this.productId)
      .subscribe((r: any) => {
          this.gridApi.hideOverlay();
          this.rowData = r.data.documents;
          this.product = r.data.product;
          this.totalCount = r.data.documents.length
        }
      );
      this.subscriptions.push(subscription);
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(s => s.unsubscribe());
  }
}
